import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { cartApi } from "../api/endpoints/cart";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

const normalizeCart = (cart) => {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const mapped = items
    .map((item) => {
      const product =
        item.productId && typeof item.productId === "object"
          ? item.productId
          : item.product || {};
      const productId = product._id || product.id || item.productId;
      if (!productId) return null;
      const rawStock = product.stock;
      const stockQty = Number.isFinite(Number(rawStock)) ? Number(rawStock) : null;
      return {
        id: String(productId),
        name: product.name || item.name || "",
        price: Number(item.price || product.price || 0),
        imgURL: product.imgURL || item.imgURL || "",
        note: item.note || "",
        qty: Number(item.quantity || item.qty || 0),
        stockQty,
        raw: item,
      };
    })
    .filter(Boolean);

  const count = mapped.reduce((sum, it) => sum + Number(it.qty || 0), 0);
  const subtotal = Number(cart?.totalPrice || 0);
  const discount = Number(cart?.appliedVoucher?.discountAmount || 0);
  const subtotalAfterDiscount = Math.max(0, subtotal - discount);
  const pricing = cart?.pricing || {
    subtotal,
    discount,
    subtotalAfterDiscount,
    deliveryFee: mapped.length ? 40 : 0,
    serviceFee: mapped.length ? Math.max(5, Math.round(subtotalAfterDiscount * 0.02 * 100) / 100) : 0,
    total: subtotalAfterDiscount,
  };

  return {
    items: mapped,
    count,
    total: subtotal,
    pricing,
    voucher: cart?.appliedVoucher || null,
  };
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [pricing, setPricing] = useState({
    subtotal: 0,
    discount: 0,
    subtotalAfterDiscount: 0,
    deliveryFee: 0,
    serviceFee: 0,
    total: 0,
  });
  const [voucher, setVoucher] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loadingById, setLoadingById] = useState({});

  const computeTotals = (nextItems) => {
    const nextCount = nextItems.reduce(
      (sum, it) => sum + Number(it.qty || 0),
      0
    );
    const nextTotal = nextItems.reduce(
      (sum, it) => sum + Number(it.qty || 0) * Number(it.price || 0),
      0
    );
    return { nextCount, nextTotal };
  };

  const applyCart = (cart) => {
    const normalized = normalizeCart(cart);
    setItems(normalized.items);
    setCount(normalized.count);
    setTotal(normalized.total);
    setPricing(normalized.pricing);
    setVoucher(normalized.voucher);
  };

  const setItemLoading = (id, value) => {
    setLoadingById((prev) => ({ ...prev, [id]: value }));
  };

  const handleAuthRedirect = () => {
    navigate("/login", { state: { from: location.pathname } });
  };

  const refreshCart = async (opts = {}) => {
    const { silent = false } = opts;
    if (!isAuthenticated) {
      setItems([]);
      setCount(0);
      setTotal(0);
      setPricing({
        subtotal: 0,
        discount: 0,
        subtotalAfterDiscount: 0,
        deliveryFee: 0,
        serviceFee: 0,
        total: 0,
      });
      setVoucher(null);
      return;
    }
    if (!silent) setIsLoading(true);
    setError("");
    try {
      const data = await cartApi.getCart();
      applyCart(data?.cart);
      if (Array.isArray(data?.adjustedItems) && data.adjustedItems.length > 0) {
        const hasSoldOut = data.adjustedItems.some(
          (item) => item.reason === "sold_out"
        );
        setNotice(hasSoldOut ? "The item you tried to buy sold out." : "");
      } else {
        setNotice("");
      }
    } catch (err) {
      if (err?.status === 401) {
        handleAuthRedirect();
        return;
      }
      setError(err?.message || "Failed to load cart");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setItems([]);
      setCount(0);
      setTotal(0);
      setPricing({
        subtotal: 0,
        discount: 0,
        subtotalAfterDiscount: 0,
        deliveryFee: 0,
        serviceFee: 0,
        total: 0,
      });
      setVoucher(null);
      setError("");
      return;
    }
    refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading]);

  // polling disabled

  const add = async (productId, qty, note) => {
    if (!isAuthenticated) return handleAuthRedirect();
    const id = String(productId);
    setItemLoading(id, true);
    setError("");
    // optimistic update
    setItems((prev) => {
      const existing = prev.find((it) => it.id === id);
      let nextItems = prev;
      if (existing) {
        nextItems = prev.map((it) =>
          it.id === id ? { ...it, qty: Number(it.qty || 0) + Number(qty || 1) } : it
        );
        if (note && typeof note === "string") {
          nextItems = nextItems.map((it) =>
            it.id === id ? { ...it, note } : it
          );
        }
      }
      const { nextCount, nextTotal } = computeTotals(nextItems);
      setCount(nextCount);
      setTotal(nextTotal);
      return nextItems;
    });
    try {
      const data = await cartApi.addItem(productId, qty, note);
      applyCart(data?.cart);
    } catch (err) {
      if (err?.status === 401) return handleAuthRedirect();
      setError(err?.message || "Failed to add item");
      refreshCart({ silent: true });
    } finally {
      setItemLoading(id, false);
    }
  };

  const setQty = async (productId, qty, note = undefined) => {
    if (!isAuthenticated) return handleAuthRedirect();
    const id = String(productId);
    setItemLoading(id, true);
    setError("");
    // optimistic update
    setItems((prev) => {
      let nextItems = prev;
      if (qty <= 0) {
        nextItems = prev.filter((it) => it.id !== id);
      } else {
        const exists = prev.find((it) => it.id === id);
        if (exists) {
          const max = exists.stockQty;
          const clampedQty =
            typeof max === "number" ? Math.min(Number(qty), max) : Number(qty);
          nextItems = prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  qty: clampedQty,
                  ...(note !== undefined ? { note } : {}),
                }
              : it
          );
        }
      }
      const { nextCount, nextTotal } = computeTotals(nextItems);
      setCount(nextCount);
      setTotal(nextTotal);
      return nextItems;
    });
    try {
      if (qty <= 0) {
        const data = await cartApi.remove(productId);
        applyCart(data?.cart);
      } else {
        const data = await cartApi.setItemQty(productId, qty, note);
        applyCart(data?.cart);
      }
    } catch (err) {
      if (err?.status === 401) return handleAuthRedirect();
      setError(err?.message || "Failed to update item");
      refreshCart({ silent: true });
    } finally {
      setItemLoading(id, false);
    }
  };

  const inc = async (productId) => {
    const current = items.find((it) => it.id === String(productId));
    const max = current?.stockQty;
    if (typeof max === "number" && Number(current?.qty || 0) >= max) return;
    const qty = Number(current?.qty || 0) + 1;
    return setQty(productId, qty);
  };

  const dec = async (productId) => {
    const current = items.find((it) => it.id === String(productId));
    const qty = Number(current?.qty || 0) - 1;
    return setQty(productId, qty);
  };

  const remove = async (productId) => setQty(productId, 0);

  const clear = async () => {
    if (!isAuthenticated) return handleAuthRedirect();
    setIsLoading(true);
    setError("");
    try {
      const data = await cartApi.clear();
      applyCart(data?.cart);
    } catch (err) {
      if (err?.status === 401) return handleAuthRedirect();
      setError(err?.message || "Failed to clear cart");
    } finally {
      setIsLoading(false);
    }
  };

  const applyVoucher = async (code) => {
    if (!isAuthenticated) return handleAuthRedirect();
    setError("");
    const data = await cartApi.applyVoucher(code);
    applyCart(data?.cart);
    return data?.cart;
  };

  const removeVoucher = async () => {
    if (!isAuthenticated) return handleAuthRedirect();
    setError("");
    const data = await cartApi.removeVoucher();
    applyCart(data?.cart);
    return data?.cart;
  };

  const qtyById = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      map[it.id] = Number(it.qty || 0);
    });
    return map;
  }, [items]);

  const value = {
    items,
    count,
    total,
    pricing,
    voucher,
    isLoading,
    error,
    notice,
    loadingById,
    qtyById,
    refreshCart,
    add,
    setQty,
    inc,
    dec,
    remove,
    clear,
    applyVoucher,
    removeVoucher,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
};






